// @ts-nocheck deprecated
import React, { FC, useMemo, useCallback, Fragment, useState, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import cls from 'classnames';

import _isEqual from 'lodash/isEqual';

import CheckIcon from '@ott/icon-check';

import useArrowKeysEvents from '../hooks/useArrowKeysEvents';

import styles from './DropdownOverlay.scss';

const OFFSET_FROM_SCREEN_BOTTOM_BORDER = 10;
const OFFSET_FROM_TRIGGER = 3;
const UIKIT_OVERLAY_HEIGHT = 504;

type Option = { id: string | number; name: string };

type Props = {
  value: any;
  checkEquality?: (option: any, value: any) => boolean;
  OptionRenderer?: FC;
  onChange: (option: any, params: any) => void;
  closeOverlay: (options: { selectNext: boolean }) => void;
  size?: 'small' | 'default';
  align?: 'left' | 'right' | 'stretch';
  className?: string;
  tabIndex: number;
  isShowEmpty: boolean;
  options: [Option];
  optionRendererProps?: {};
  container: HTMLElement;
  overlayRef;
  resolveIdField: () => void;
  resolveOptionKey: (option: any, i: number) => any;
  specificLocators?: string | [string];
  portalContainerRef;
  shouldRenderAirportCity: boolean;
  shouldUseCheckBox: boolean;
  shouldUseEllipsis: boolean;
  isMultiple: boolean;
  childrenClassNames?: {
    option?: string;
  };
};

const DropdownNextOverlay: FC<Props> = (props) => {
  const {
    value,
    checkEquality = _isEqual,
    OptionRenderer = DefaultOptionRenderer,
    onChange,
    closeOverlay,
    size = 'default',
    align = 'left',
    className = '',
    tabIndex,
    isShowEmpty,
    options,
    optionRendererProps = {},
    container,
    overlayRef,
    resolveIdField,
    resolveOptionKey: resolveOptionKeyProp = defaultResolveOptionKey,
    specificLocators = [],
    portalContainerRef,
    shouldRenderAirportCity,
    shouldUseCheckBox,
    shouldUseEllipsis,
    isMultiple,
    childrenClassNames,
  } = props;
  // отслеживание изменения высоты области просмотра
  const [viewPortHeight, setViewPortHeight] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);

  // Отслеживаем рассчитались ли высоты области просмотра
  const [isHeightsCalculated, setHeightsCalculated] = useState(false);

  const handleResize = useCallback(() => {
    if (__WHITE_LABEL__) {
      (async () => {
        const { default: adapter } = await import('@ott/white-label/adapter');
        const { innerHeight } = await adapter.getWindowData();
        setViewPortHeight(innerHeight);
        setWindowHeight(document.documentElement.clientHeight);
        setHeightsCalculated(true);
      })();
    } else {
      setViewPortHeight(document.documentElement.clientHeight);
      setWindowHeight(document.documentElement.clientHeight);
    }
  }, [setWindowHeight, setViewPortHeight]);
  useEffect(() => {
    handleResize();
  }, []);
  useEffect(() => {
    if (__WHITE_LABEL__) {
      (async () => {
        const { default: adapter, EVENTS } = await import('@ott/white-label/adapter');
        adapter.on(EVENTS.FRAME_RESIZE, handleResize);
      })();
      return async () => {
        const { default: adapter, EVENTS } = await import('@ott/white-label/adapter');
        adapter.off(EVENTS.FRAME_RESIZE, handleResize);
      };
    }
    document.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // отслеживание скрола страницы
  const [scrollTop, setScrollTop] = useState(0);
  const [isScrollCalculated, setScrollCalculated] = useState(false);
  useEffect(() => {
    if (__WHITE_LABEL__) {
      (async () => {
        const { default: adapter } = await import('@ott/white-label/adapter');
        const { iframeYOffset } = await adapter.getWindowData();
        setScrollTop(iframeYOffset);
      })();
    } else {
      setScrollTop(window.pageYOffset);
    }
    setScrollCalculated(true);
  }, []);
  useEffect(() => {
    const handleScroll = (event) => {
      if (event.whitelabel) {
        setScrollTop(event.scrollTop);
      } else {
        setScrollTop(window.pageYOffset);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [setScrollTop]);

  const position = useMemo(() => {
    if (!container) {
      return {};
    }

    const {
      width: containerWidth,
      left: containerLeft,
      right: containerRight,
      bottom: containerBottom,
    } = container.getBoundingClientRect();

    const calculatedHeight = windowHeight - containerBottom;
    const positionTop = (__WHITE_LABEL__ ? 0 : scrollTop) + containerBottom + OFFSET_FROM_TRIGGER;
    const viewPortBottom = viewPortHeight + scrollTop;
    const maxHeight = Math.min(
      calculatedHeight,
      viewPortBottom - positionTop,
      UIKIT_OVERLAY_HEIGHT
    );

    const basePosition = {
      top: positionTop,
      overlayY: 'auto',
      maxHeight: maxHeight - OFFSET_FROM_SCREEN_BOTTOM_BORDER,
      minWidth: containerWidth,
    };

    const offsetX = window ? window.pageXOffset : 0;
    const windowWidth = document.documentElement.clientWidth;

    if (align === 'right') {
      return {
        ...basePosition,
        right: windowWidth - containerRight,
      };
    }

    if (align === 'stretch') {
      return {
        ...basePosition,
        left: containerLeft + offsetX,
        right: windowWidth - containerRight,
      };
    }

    return {
      ...basePosition,
      left: containerLeft + offsetX,
    };
  }, [container, windowHeight, viewPortHeight, scrollTop]);

  const index = useMemo(
    () => (options || []).findIndex((option) => checkEquality(option, value)),
    [value, checkEquality, options]
  );

  useArrowKeysEvents({
    index,
    options,
    onChange,
    isMultiple,
  });

  const handleChange = useCallback(
    (option, params) => {
      onChange(option, params);
      if (!params || params.selectActive) {
        closeOverlay({ selectNext: true });
      }
    },
    [onChange, closeOverlay]
  );

  const checkIsActive = (option, i) => {
    if (isMultiple) {
      const activeIds = value.map((element) => element.id);

      return activeIds.includes(option.id);
    }

    return index === i;
  };

  // не ставить этот if выше useArrowKeysEvents,
  // работать будет без ошибки "Rendered fewer hooks than expected"
  // но в некоторых кейсах useArrowKeysEvents будет без unmount шага
  if (!isScrollCalculated || (!isShowEmpty && (!options || !options.length))) {
    return null;
  }

  let resolveOptionKey = resolveOptionKeyProp;
  if (resolveIdField) {
    resolveOptionKey = resolveIdField;
  }

  if (__WHITE_LABEL__ && !isHeightsCalculated) {
    return null;
  }

  return createPortal(
    <div
      ref={overlayRef}
      className={cls(styles.overlay, className)}
      tabIndex={tabIndex}
      style={position}
      data-locator={cls('dropdown-overlay', specificLocators)}
    >
      <div className={styles.inner}>
        {(options || []).map((option, i) => (
          <OptionRenderer
            key={resolveOptionKey(option, i)}
            option={option}
            isActive={checkIsActive(option, i)}
            onChange={handleChange}
            className={cls(styles[size], childrenClassNames?.option)}
            shouldRenderAirportCity={shouldRenderAirportCity}
            shouldUseEllipsis={shouldUseEllipsis}
            shouldUseCheckBox={shouldUseCheckBox}
            isMultiple={isMultiple}
            {...optionRendererProps}
          />
        ))}
      </div>
    </div>,
    (portalContainerRef && portalContainerRef.current) || document.body
  );
};

export const defaultResolveContentField = (option) => {
  if (option) {
    return option.name;
  }

  return null;
};

export const defaultResolveLocatorField = (option) => {
  if (option) {
    return option.specificLocators;
  }

  return null;
};

const defaultResolveOptionKey = (option, index) => {
  if (option) {
    if (option.hasOwnProperty('key')) {
      return option.key;
    }

    if (option.hasOwnProperty('id')) {
      return option.id;
    }
  }

  return index;
};

type DefaultOptionRendererProps = {
  option: Option;
  className?: string;
  onChange: (option: Option, options: { selectActive: boolean; selectNext: boolean }) => void;
  isActive: boolean;
  resolveContentField?: (option: Option) => ReactNode;
  resolveLocatorField?: (option: Option) => string;
  shouldUseCheckBox: boolean;
  isMultiple: boolean;
};

export const DefaultOptionRenderer: FC<DefaultOptionRendererProps> = ({
  option,
  className = '',
  onChange,
  isActive,
  resolveContentField = defaultResolveContentField,
  resolveLocatorField = defaultResolveLocatorField,
  shouldUseCheckBox,
  isMultiple,
}) => {
  const handleChange = useCallback(() => {
    onChange(option, { selectActive: !isMultiple, selectNext: true });
  }, [option, onChange]);

  return (
    <Fragment>
      <label
        className={cls(styles.Option, className, {
          [styles.showWithCheckboxes]: shouldUseCheckBox,
        })}
        onClick={handleChange}
        data-locator={resolveLocatorField(option)}
      >
        <span className={cls(styles.name)}>{resolveContentField(option)}</span>
        {shouldUseCheckBox ? (
          <div className={cls(styles.checkbox, { [styles.isActive]: isActive })}>
            <CheckIcon
              className={cls(styles.icon, styles.isInCheckbox, { [styles.checked]: isActive })}
            />
          </div>
        ) : (
          <CheckIcon className={cls(styles.icon, { [styles.checked]: isActive })} />
        )}
      </label>
    </Fragment>
  );
};

DefaultOptionRenderer.displayName = 'DefaultOptionRenderer';
DropdownNextOverlay.displayName = 'DropdownNextOverlay';

export default DropdownNextOverlay;
